// useChatSocket — Socket.IO hook for real-time direct chat
// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import useChatStore from "@/stores/chatStore";
import { directChatAPI } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Socket.IO server URL (just the host, no path)
const SOCKET_URL = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");

export function useChatSocket(user) {
  const socketRef = useRef(null);
  const storeRef = useRef(useChatStore.getState());

  // Keep store ref in sync
  useEffect(() => {
    const unsub = useChatStore.subscribe((state) => {
      storeRef.current = state;
    });
    return unsub;
  }, []);

  const loadUnreadCounts = useCallback(async () => {
    try {
      const res = await directChatAPI.getUnreadCount();
      useChatStore.getState().setUnreadCounts(
        res.data.total_unread, 
        res.data.by_conversation
      );
    } catch (err) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    // Load initial unread counts
    loadUnreadCounts();

    // Create a dedicated socket connection for chat
    // path must be "/ws/socket.io" to match nginx proxy config
    const socket = io(SOCKET_URL, {
      path: "/ws/socket.io",
      forceNew: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("[DirectChat] Socket connected, sid:", socket.id);
      socket.emit("chat:authenticate", { token });
    });

    socket.on("chat:authenticated", (data) => {
      if (data.status === "ok") {
        console.log("[DirectChat] Authenticated as:", data.user_id);
      } else {
        console.error("[DirectChat] Auth failed:", data.detail);
      }
    });

    socket.on("chat:new_message", (data) => {
      console.log("[DirectChat] New message received:", data);
      const { message, sender, conversation_id } = data;
      const store = storeRef.current;
      
      if (store.chatOpen && store.activeConversationId === conversation_id) {
        window.dispatchEvent(
          new CustomEvent("chat:incoming_message", { detail: data })
        );
        return;
      }

      useChatStore.getState().incrementUnread(conversation_id);
      useChatStore.getState().showNotification({
        message,
        sender,
        conversationId: conversation_id,
      });
    });

    socket.on("chat:typing", (data) => {
      window.dispatchEvent(
        new CustomEvent("chat:typing_indicator", { detail: data })
      );
    });

    socket.on("chat:messages_read", (data) => {
      window.dispatchEvent(
        new CustomEvent("chat:read_receipt", { detail: data })
      );
    });

    socket.on("connect_error", (err) => {
      console.error("[DirectChat] Connection error:", err.message);
    });

    socket.on("disconnect", (reason) => {
      console.log("[DirectChat] Disconnected:", reason);
    });

    return () => {
      console.log("[DirectChat] Cleaning up socket");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id]);

  const emitTyping = useCallback((targetUserId, conversationId, isTyping) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit("chat:typing", {
        target_user_id: targetUserId,
        conversation_id: conversationId,
        is_typing: isTyping,
      });
    }
  }, []);

  return { socketRef, emitTyping, loadUnreadCounts };
}
